/**
 * Preview list sorting — enabled items first, then disabled; tie-break by name.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FlowOptimizerItemPreview } from '@trafficpulse/shared';

import type { PreviewRow } from './preview-row.types';

type ActiveNamed = {
  active?: boolean;
  name?: string;
  itemName?: string;
};

function isActive(item: ActiveNamed): boolean {
  return item.active !== false;
}

function displayName(item: ActiveNamed): string {
  return (item.name ?? item.itemName ?? '').trim().toLowerCase();
}

export function sortByActiveThenName<T extends ActiveNamed>(a: T, b: T): number {
  const activeDiff = Number(isActive(b)) - Number(isActive(a));
  if (activeDiff !== 0) return activeDiff;
  return displayName(a).localeCompare(displayName(b));
}

export type PreviewListSortMode = 'rank' | 'name';

export type PreviewRankFilter = 'all' | 'top5' | 'top10';

export function previewItemKey(item: FlowOptimizerItemPreview): string {
  return `${item.layer}-${item.itemId}-${item.parentId ?? ''}`;
}

export function sortPreviewItems(
  items: ReadonlyArray<FlowOptimizerItemPreview>,
): FlowOptimizerItemPreview[] {
  return [...items].sort(sortByActiveThenName);
}

export function sortPreviewItemsByRank(
  items: ReadonlyArray<FlowOptimizerItemPreview>,
): FlowOptimizerItemPreview[] {
  return [...items].sort((a, b) => {
    const activeDiff = Number(isActive(b)) - Number(isActive(a));
    if (activeDiff !== 0) return activeDiff;
    const scoreDiff = (b.rawScore ?? 0) - (a.rawScore ?? 0);
    if (scoreDiff !== 0) return scoreDiff;
    // Tie-break by name then itemId — must match ranking.ts buildOrder so the
    // ⚡ tags agree with these #N rank badges on equal-score ties.
    const nameDiff = displayName(a).localeCompare(displayName(b));
    if (nameDiff !== 0) return nameDiff;
    return (a.itemId ?? '').localeCompare(b.itemId ?? '');
  });
}

export function sortPreviewItemsByMode(
  items: ReadonlyArray<FlowOptimizerItemPreview>,
  mode: PreviewListSortMode,
): FlowOptimizerItemPreview[] {
  return mode === 'rank' ? sortPreviewItemsByRank(items) : sortPreviewItems(items);
}

export function rankPreviewItems(
  items: ReadonlyArray<FlowOptimizerItemPreview>,
): Map<string, number> {
  const ranks = new Map<string, number>();
  sortPreviewItemsByRank(items).forEach((item, index) => {
    ranks.set(previewItemKey(item), index + 1);
  });
  return ranks;
}

export function maxRankForFilter(filter: PreviewRankFilter): number | null {
  if (filter === 'top5') return 5;
  if (filter === 'top10') return 10;
  return null;
}

export function filterPreviewItemsByRank(
  items: ReadonlyArray<FlowOptimizerItemPreview>,
  ranks: ReadonlyMap<string, number>,
  maxRank: number | null,
): FlowOptimizerItemPreview[] {
  if (maxRank == null) return [...items];
  return items.filter((item) => (ranks.get(previewItemKey(item)) ?? Infinity) <= maxRank);
}

export function sortPreviewRows(rows: ReadonlyArray<PreviewRow>): PreviewRow[] {
  return [...rows].sort(sortByActiveThenName);
}

// ---------------------------------------------------------------------------
// View-only metric sort for the Preview tab column headers (Current/New/Diff).
// Active items always stay first (inactive sink to the bottom, as today); the
// chosen metric orders within each group; name is the final tiebreak. `null`
// sort == the default active-then-name order. This never mutates config order.
// ---------------------------------------------------------------------------

export type PreviewSortColumn = 'current' | 'new' | 'diff';

export interface PreviewMetricSort {
  column: PreviewSortColumn;
  dir: 'asc' | 'desc';
}

export interface PreviewSortableEntry {
  active: boolean;
  name: string;
  row: PreviewRow | null;
}

function previewMetricValue(row: PreviewRow | null, column: PreviewSortColumn): number {
  if (!row) return 0;
  if (column === 'current') return row.currentPerMille;
  if (column === 'new') return row.optimalPerMille;
  return row.deltaPerMille;
}

export function comparePreviewEntries(
  a: PreviewSortableEntry,
  b: PreviewSortableEntry,
  sort: PreviewMetricSort | null,
): number {
  const activeDiff = Number(b.active) - Number(a.active);
  if (activeDiff !== 0) return activeDiff;
  if (sort) {
    const raw = previewMetricValue(a.row, sort.column) - previewMetricValue(b.row, sort.column);
    const signed = sort.dir === 'asc' ? raw : -raw;
    if (signed !== 0) return signed;
  }
  return a.name.trim().toLowerCase().localeCompare(b.name.trim().toLowerCase());
}

/** Header click cycle: (inactive) → desc → asc → off. */
export function cyclePreviewSort(
  prev: PreviewMetricSort | null,
  column: PreviewSortColumn,
): PreviewMetricSort | null {
  if (!prev || prev.column !== column) return { column, dir: 'desc' };
  if (prev.dir === 'desc') return { column, dir: 'asc' };
  return null;
}

export function normalizePreviewEntityId(itemId: string | number): string {
  return String(itemId).trim();
}

/**
 * Collapse repeated lander/offer preview rows that share the same entity id.
 * The walker emits one row per (path, lander) group, so the same offer can
 * appear several times even when it is a single Binom/Voluum entity.
 */
export function dedupePreviewItems(
  items: ReadonlyArray<FlowOptimizerItemPreview>,
): FlowOptimizerItemPreview[] {
  const byId = new Map<string, FlowOptimizerItemPreview>();
  for (const item of items) {
    const key = normalizePreviewEntityId(item.itemId);
    const existing = byId.get(key);
    if (!existing || shouldPreferPreviewItem(item, existing)) {
      byId.set(key, item);
    }
  }
  return [...byId.values()];
}

function shouldPreferPreviewItem(
  next: FlowOptimizerItemPreview,
  current: FlowOptimizerItemPreview,
): boolean {
  if (next.active !== current.active) return next.active;
  return Math.abs(next.deltaPerMille) > Math.abs(current.deltaPerMille);
}
